// CC: taken from reviewers.component.ts (Inbound Shield > Reviewers) — card-based list for the onboarding wizard
import * as util from 'util';
import _ from 'lodash';
import {GeneralService} from '../../../services/generalService';
import {RouteService} from '../../../services/routeService';
import {NotificationService} from '../../../services/notificationService';
import {DICTIONARY} from '../../../dictionary';
import {Component, OnInit} from "@angular/core";

@Component({
	selector: 'onboarding-reviewers-component',
	templateUrl: './onboarding-reviewers.component.html',
})
export class OnboardingReviewersComponent implements OnInit {
	dic = DICTIONARY;
	config;
	loadingInProcess = true;
	actionInProcess;
	searchTerm = '';
	addReviewerPopup;
	usersSelectionPopup;
	selectedUsers;
	assignReviewer;
	assignReviewerSelectedGroups;
	assignReviewerToGroupsPopup;
	assignReviewerToMailboxesPopup;

	// CC: single source for the permission chips and the expanded toggles, instead of the seven
	// CC: near-identical handlers in reviewers.component.ts (the "action" value is the one the backend expects)
	permissions = [
		{
			key: 'allow_view_content',
			action: 'viewContent',
			label: 'View Content',
			description: 'Allow reviewer to view the email content for quarantined emails'
		},
		{
			key: 'reviewer_allow_release_malicious',
			action: 'maliciousEmails',
			label: 'Release Malicious',
			description: 'Allow reviewer to release malicious emails'
		},
		{
			key: 'allow_change_configuration',
			action: 'changeConfiguration',
			label: 'Change Configuration',
			description: 'When enabled - the reviewer can manage all inbound shield settings. When disabled - the reviewer will only have access to the "Email Activity" page and actions within it'
		},
		{
			key: 'threat_response_permission',
			action: 'threatResponsePermission',
			label: 'Threat Response',
			description: 'Allow reviewer to access the "Threat Response" functionality'
		},
		{
			key: 'client_defang_malicious_url',
			action: 'Defang Malicious URL',
			label: 'Defang Malicious URLs',
			description: 'URLs that have been detected as malicious will be defanged and become unclickable in the "Threat Analysis" view for increased security'
		},
		{
			key: 'allow_trends_page',
			action: 'Trends and Insights',
			label: 'Trends and Insights',
			description: 'Allow reviewer to access the Trends and Insights page'
		},
		{
			key: 'global_allow_block_list',
			action: 'globalAllowBlockLists',
			label: 'Global Allow/Block lists',
			description: 'Allow reviewer to access & update global allow/block lists'
		}
	];

	constructor(public gs: GeneralService,
				private rs: RouteService,
				private ns: NotificationService) {
	}

	ngOnInit() {
		this.getInboundReviewers();
	}

	// CC: taken from reviewers.component.ts -> getInboundReviewers
	getInboundReviewers = () => {
		this.loadingInProcess = true;
		this.rs.getInboundReviewers().then((response) => {
			this.config = response;
			this.config.reviewers.forEach(reviewerObj => {
				this.setReviewerLabels(reviewerObj);
			});

			this.loadingInProcess = false;
		}, () => {
			this.loadingInProcess = false;
		});
	};

	// CC: labels are calculated once per change so the template doesn't call functions
	setReviewerLabels = (reviewerObj) => {
		reviewerObj.groupsLabel = reviewerObj.groups?.length ? _.map(reviewerObj.groups, 'name').join(', ') : '';
		reviewerObj.mailboxesLabel = reviewerObj.mailboxes?.length ? reviewerObj.mailboxes.join(', ') : '';
	};

	togglePermissions = (reviewerObj) => {
		reviewerObj.isExpanded = !reviewerObj.isExpanded;
	};

	searchReviewers = () => {
		const searchTerm = this.searchTerm.toLowerCase();
		this.config.reviewers.forEach(reviewerObj => {
			reviewerObj.hide = !!searchTerm && !reviewerObj.email?.toLowerCase().includes(searchTerm);
		});
	};

	openAddReviewer = () => {
		this.addReviewerPopup = {
			show: true,
			applyInProcess: false,
			email: '',
			errMsg: '',
		};
	};

	addReviewer = () => {
		const email = _.trim(this.addReviewerPopup.email);
		if (!email) {
			return;
		}
		if (!this.gs.validateEmail(email)) {
			this.addReviewerPopup.errMsg = util.format(this.dic.ERRORS.invalidEmail, email);
			return;
		}
		if (_.find<any>(this.config.reviewers, reviewerObj => reviewerObj.email === email)) {
			this.addReviewerPopup.errMsg = util.format(this.dic.ERRORS.emailAlreadyExist, email);
			return;
		}

		this.addReviewerPopup.applyInProcess = true;
		this.addReviewers([email]).then(() => {
			this.addReviewerPopup.show = false;
		}, () => {
			this.addReviewerPopup.applyInProcess = false;
		});
	};

	// CC: taken from reviewers.component.ts -> startAddFromUsersSelection
	startAddFromUsersSelection = (users) => {
		const existingReviewerEmails = _.map(this.config.reviewers, 'email');
		const newEmails = users.filter(user => !existingReviewerEmails.includes(user));

		if (newEmails.length) {
			this.addReviewers(newEmails);
		}
	};

	// CC: taken from reviewers.component.ts -> startRemoveFromUsersSelection
	startRemoveFromUsersSelection = (users) => {
		const reviewersToRemove = this.config.reviewers.filter(reviewerObj => users.includes(reviewerObj.email));

		if (reviewersToRemove.length) {
			this.removeReviewers(reviewersToRemove);
		}
	};

	openUsersSelection = () => {
		this.selectedUsers = _.map(this.config.reviewers, 'email');
		this.usersSelectionPopup = {
			show: true
		};
	};

	// CC: taken from reviewers.component.ts -> addReviewers, simplified (this list has no inline edit mode)
	addReviewers = (emails) => {
		const flags = {
			allow_view_content: true,
			reviewer_allow_release_malicious: true,
			allow_change_configuration: true,
			threat_response_permission: true,
			client_defang_malicious_url: false,
			allow_trends_page: false,
			global_allow_block_list: true
		};

		const data = {
			action: this.dic.CONSTANTS.inboundConfigurationsActions.add,
			emails: emails,
			flags: flags
		};

		this.actionInProcess = true;
		return this.rs.updateInboundReviewers(data).then(() => {
			emails.forEach(email => {
				this.config.reviewers.push({
					email: email,
					is_primary: !this.config.reviewers.length,
					allow_view_content: flags.allow_view_content,
					reviewer_allow_release_malicious: flags.reviewer_allow_release_malicious,
					allow_change_configuration: flags.allow_change_configuration,
					threat_response_permission: flags.threat_response_permission,
					client_defang_malicious_url: flags.client_defang_malicious_url,
					allow_trends_page: flags.allow_trends_page,
					global_allow_block_list: flags.global_allow_block_list,
					groupsLabel: '',
					mailboxesLabel: '',
					isExpanded: false,
					hide: false
				});
			});

			this.actionInProcess = false;
			this.ns.showInfoMessage(util.format(this.dic.MESSAGES.itemAdded, emails.length > 1 ? emails.length + ' reviewers' : emails[0]));
		}, (error) => {
			this.actionInProcess = false;

			if (error?.data?.userNotInPlan) {
				const message = util.format(this.dic.ERRORS.tpUserNotPartOfPlan,
					'<a target="_self" href="/' + this.dic.CONSTANTS.appStates.adminOutbound + '/' + this.dic.CONSTANTS.adminPages.outbound.userManagement + '"><b><i>User Management</i></b></a>');

				this.ns.showErrorMessage(message);
			}

			return Promise.reject(error);
		});
	};

	// CC: taken from reviewers.component.ts -> removeReviewers
	removeReviewers = (reviewers) => {
		if (!reviewers?.length) {
			return;
		}

		let body = [];
		const emails = _.map(reviewers, 'email');
		const emailsRemovedText = emails.length === 1 ? emails[0] : emails.length + ' emails';
		const isPrimary = _.find<any>(reviewers, reviewerObj => emails.includes(reviewerObj.email) && reviewerObj.is_primary);
		if (isPrimary) {
			body.push('The "primary" status will be assigned to the next unassigned reviewer in your list');
		}

		this.gs.showPopup({
			title: 'Remove reviewer',
			subTitle: `Are you sure you want to remove ${emailsRemovedText}?`,
			body: body,
			type: this.dic.CONSTANTS.popupWarning,
			doneBtnText: this.dic.CONSTANTS.inboundConfigurationsActions.remove,
			doneCb: () => {
				let data = {
					action: this.dic.CONSTANTS.inboundConfigurationsActions.remove,
					emails: []
				};

				emails.forEach(email => {
					if (this.gs.validateEmail(email)) {
						data.emails.push(email);
					}
				});

				this.actionInProcess = true;

				this.rs.updateInboundReviewers(data).then(() => {
					const deletedReviewers = _.remove<any>(this.config.reviewers, reviewerObj => emails.includes(reviewerObj.email));
					// primary reviewer was deleted
					if (_.find<any>(deletedReviewers, reviewerObj => reviewerObj.is_primary) && this.config.reviewers.length) {
						this.config.reviewers[0].is_primary = true;
					}

					this.actionInProcess = false;
					this.ns.showInfoMessage(util.format(this.dic.MESSAGES.itemRemoved, emailsRemovedText));
				}, () => {
					this.actionInProcess = false;
				});
			}
		});
	};

	// CC: taken from reviewers.component.ts -> setReviewerAsPrimary
	setReviewerAsPrimary = (reviewerObj) => {
		this.gs.showPopup({
			title: "Set reviewer as primary",
			subTitle: `You are about to set ${reviewerObj.email} as a primary reviewer`,
			body: [
				'The primary reviewer will receive notifications for all groups/mailboxes for which no reviewer is specified',
				'The primary reviewer can also be assigned as a group reviewer'
			],
			type: this.dic.CONSTANTS.popupInfo,
			doneBtnText: 'Set',
			doneCb: () => {
				let data = {
					action: this.dic.CONSTANTS.reviewerActions.setAsPrimary,
					emails: [reviewerObj.email]
				};

				this.rs.updateInboundReviewers(data).then(() => {
					this.config.reviewers.forEach(currentReviewer => {
						currentReviewer.is_primary = false;
					});
					reviewerObj.is_primary = true;
					this.ns.showInfoMessage(this.dic.MESSAGES.setReviewerAsPrimary);
				});
			}
		});
	};

	// CC: taken from reviewers.component.ts -> the seven per-permission handlers
	updateReviewerPermission = (reviewerObj, permission) => {
		const data = {
			action: permission.action,
			emails: [reviewerObj.email],
			enabled: reviewerObj[permission.key]
		};

		this.actionInProcess = true;
		this.rs.updateInboundReviewers(data).then(() => {
			this.actionInProcess = false;
			this.ns.showInfoMessage(this.dic.MESSAGES.operationsSuccess);
		}, () => {
			// CC: revert the toggle so the card keeps showing the permission that is actually stored
			reviewerObj[permission.key] = !reviewerObj[permission.key];
			this.actionInProcess = false;
		});
	};

	// CC: taken from reviewers.component.ts -> openAssignReviewerToGroupsPopup
	openAssignReviewerToGroupsPopup = (show, reviewerObj = null) => {
		if (show) {
			this.assignReviewer = reviewerObj;
			this.assignReviewerSelectedGroups = reviewerObj.groups ? reviewerObj.groups.map(groupObj => groupObj.email) : null;
		}
		this.assignReviewerToGroupsPopup = show;
	};

	// CC: taken from reviewers.component.ts -> applyReviewerToGroups
	applyReviewerToGroups = (groups) => {
		const groupsSelected = _.map(_.filter(groups, 'selected'), groupObj => {
			return {
				email: groupObj.email,
				name: groupObj.displayName,
				group_id: groupObj._id
			};
		});

		const data = {
			action: this.dic.CONSTANTS.reviewerActions.assignGroups,
			emails: [this.assignReviewer.email],
			groups: groupsSelected
		};

		this.rs.updateInboundReviewers(data).then(() => {
			this.assignReviewerToGroupsPopup = false;
			this.assignReviewer.groups = groupsSelected;
			this.setReviewerLabels(this.assignReviewer);
			this.ns.showInfoMessage(this.dic.MESSAGES.groupsAssigned);
		});
	};

	// CC: taken from reviewers.component.ts -> openAssignReviewerToMailboxesPopup
	openAssignReviewerToMailboxesPopup = (show, reviewerObj = null) => {
		this.assignReviewerToMailboxesPopup = {
			show,
			reviewerObj,
			mailboxesInProcess: true,
			only_selected_mailboxes: reviewerObj.only_selected_mailboxes
		};

		this.rs.getMailboxesInfo().then(data => {
			this.assignReviewerToMailboxesPopup.mailboxes = data;
			this.assignReviewerToMailboxesPopup.mailboxes.forEach((mailboxObj) => {
				if (reviewerObj.mailboxes?.includes(mailboxObj.email)) {
					mailboxObj.selected = true;
				}
			});
			this.assignReviewerToMailboxesPopup.mailboxesInProcess = false;
		});
	};

	// CC: taken from reviewers.component.ts -> applyReviewerToMailboxes
	applyReviewerToMailboxes = () => {
		this.assignReviewerToMailboxesPopup.actionInProcess = true;

		const mailboxesSelected = _.map(_.filter(this.assignReviewerToMailboxesPopup.mailboxes, 'selected'), mailboxObj => {
			return mailboxObj.email;
		});

		const reviewerObj = this.assignReviewerToMailboxesPopup.reviewerObj;
		const data = {
			action: this.dic.CONSTANTS.reviewerActions.assignMailboxes,
			emails: [reviewerObj.email],
			only_selected_mailboxes: this.assignReviewerToMailboxesPopup.only_selected_mailboxes,
			mailboxes: mailboxesSelected
		};

		this.rs.updateInboundReviewers(data).then(() => {
			reviewerObj.mailboxes = mailboxesSelected;
			reviewerObj.only_selected_mailboxes = this.assignReviewerToMailboxesPopup.only_selected_mailboxes;
			this.setReviewerLabels(reviewerObj);
			this.assignReviewerToMailboxesPopup = null;
			this.ns.showInfoMessage(this.dic.MESSAGES.mailboxesAssigned);
		}, () => {
			this.assignReviewerToMailboxesPopup.actionInProcess = false;
		});
	};

	// CC: taken from reviewers.component.ts -> searchMailbox
	searchMailbox = (event) => {
		this.assignReviewerToMailboxesPopup.mailboxes.forEach(record => {
			if (event.searchTerm) {
				const isFound = searchMailboxExecute(record, event.searchTerm);
				if (!isFound) {
					record.hide = true;
					return;
				}
			}

			record.hide = false;
		});
	};

	// CC: taken from reviewers.component.ts -> updateNotifyReviewerViewContent
	updateNotifyReviewerViewContent = () => {
		const tpData = {
			type: this.dic.CONSTANTS.rulesTpAction.notifyReviewerViewContent,
			enabled: this.config.notify_reviewer_view_content,
		};

		this.actionInProcess = true;
		this.rs.updateTpRules('', tpData).then(() => {
			this.actionInProcess = false;
		}, () => {
			this.actionInProcess = false;
		});
	};
}

// CC: taken from reviewers.component.ts -> searchMailboxExecute
function searchMailboxExecute(mailboxObj, searchTerm) {
	searchTerm = searchTerm.toLowerCase();
	return (mailboxObj.email?.toLowerCase().includes(searchTerm) || mailboxObj.name?.toLowerCase().includes(searchTerm));
}
